import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';

import * as Analytics from 'analytics/Analytics';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import getWorksheetPersistentStorage from 'notebook/notes/WorksheetPersistentStorage';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import WorkspaceStore from 'sidebar/WorkspaceStore';

import WorksheetTabState from './WorksheetTabState';
import WorksheetView from './WorksheetView';

export default class WorksheetTabView extends Backbone.View<WorksheetTabState> {
  static initClass(): void {
    this.prototype.className = 'buffer-tab buffer-tab--no-padding';
  }

  initialize(): void {
    this.listenTo(this.model, 'change:id', () => {
      // unset the selected step ID.
      // WorksheetDetails component will select the first available step
      // if selectedStepId is undefined
      this.setSelectedStepId(undefined);
      this.render();
    });
  }

  /**
   * Set the selected step ID into the URL anchor, persisted storage
   * @param stepId the step ID to set
   */
  setSelectedStepId(stepId?: string): void {
    if (stepId) {
      window.location.hash = `#${stepId}`;
    }
    getWorksheetPersistentStorage().setSelectedStepId(this.model.id, stepId);
  }

  render(): this {
    const id = this.model.get('id');

    /** Returns true if onSetWorksheet switches active tabs, false if it opens the worksheet in the current tab.
     * This is used to inform if we should find the corresponding step to select when opening in the same tab.
     */
    const onSetWorksheet = (id: string): boolean => {
      const workspaceItems = WorkspaceStore.getState().workspaceItems;
      const workspaceContainsOpenId = workspaceItems.find((item) => item.fileId === id);
      if (workspaceContainsOpenId) {
        // workspaceItems has an instance of the fileId open, so we will switch to that tab
        FileBrowserActions.openFile(id);
        return true;
      } else {
        const worksheetFileState = this.model.getWorksheetFileState();
        if (worksheetFileState.hasUnsavedChanges()) {
          // eslint-disable-next-line no-alert
          const confirmSwitchingWithUnsavedChanges = window.confirm(
            `"${worksheetFileState.name()}" has unsaved changes. Do you want to switch Worksheets and discard your changes?`
          );
          // returns without setting ID on the fileState
          if (!confirmSwitchingWithUnsavedChanges) {
            return false;
          }
        }
        worksheetFileState.setId(id);
        return false;
      }
    };

    renderToDOM(<WorksheetView id={id} onSetWorksheet={onSetWorksheet} />, this.el);
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.VIEWED_WORKSPACE_ITEM,
      ANALYTICS_MODULE.WORKSHEETS,
      {
        event_group_id: Analytics.makeAnalyticsEventId(),
        action_origin: ActionOrigin.UI_UNDETERMINED,
        viewed_object_id: id,
        viewed_object_type: 'worksheet',
      }
    );
    return this;
  }

  remove(): Backbone.View<WorksheetTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

WorksheetTabView.initClass();
